import {
    AlertCircle,
    ArrowLeft,
    CheckCircle2,
    Eye,
    EyeOff,
    KeyRound,
    Lock,
    Mail,
    RotateCcw,
    Send,
} from "lucide-react";
import React, { FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../store";
import {
    clearState,
    forgotPassword,
    sendOtp,
} from "../../../Redux/custmoer_auth/slice";
import {
    forgotPasswordContent,
    forgotPasswordRoutes,
    forgotPasswordSocialLinks,
} from "./ForgotPasswordData";
import {
    ForgotPasswordFormErrors,
    ForgotPasswordStep,
} from "./ForgotPasswordTypes";
import "./ForgotPasswordPage.css";

const ForgotPasswordPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { authloading, autherror, success, message } = useSelector(
        (state: RootState) => state.customerAuth
    );

    const [step, setStep] = useState<ForgotPasswordStep>(1);
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<ForgotPasswordFormErrors>({});
    const [isResetSuccess, setIsResetSuccess] = useState(false);

    // Clear Redux customerAuth state on mount and unmount
    useEffect(() => {
        dispatch(clearState());
        return () => {
            dispatch(clearState());
        };
    }, [dispatch]);

    // Handle Redux success transitions
    useEffect(() => {
        if (success && step === 1) {
            if (message) toast.success(message);
            setStep(2);
            setErrors({});
            dispatch(clearState());
        } else if (success && step === 2) {
            if (message) toast.success(message);
            setIsResetSuccess(true);
            const timer = setTimeout(() => {
                navigate(forgotPasswordRoutes.signIn);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [success, step, message, navigate, dispatch]);

    // Handle Redux errors
    useEffect(() => {
        if (autherror) {
            toast.error(autherror);
            setErrors((prev) => ({ ...prev, api: autherror }));
        }
    }, [autherror]);

    // Email input sanitizer (max 30 chars, max 6 digits, allowed characters only)
    const handleEmailChange = (value: string) => {
        const stripped = value.replace(/[^a-zA-Z0-9@.]/g, "");
        const digitCount = (stripped.match(/\d/g) || []).length;
        if (stripped.length > 30 || digitCount > 6) return;
        setEmail(stripped);
        setErrors((prev) => ({ ...prev, email: undefined, api: undefined }));
    };

    // OTP input sanitizer (no spaces, max 10 chars)
    const handleOtpChange = (value: string) => {
        const stripped = value.replace(/\s/g, "").slice(0, 10);
        setOtp(stripped);
        setErrors((prev) => ({ ...prev, otp: undefined, api: undefined }));
    };

    // New password sanitizer (no spaces, max 15 chars)
    const handleNewPasswordChange = (value: string) => {
        const stripped = value.replace(/\s/g, "").slice(0, 15);
        setNewPassword(stripped);
        setErrors((prev) => ({
            ...prev,
            newPassword: undefined,
            confirmPassword: undefined,
            api: undefined,
        }));
    };

    // Confirm password sanitizer (no spaces, max 15 chars)
    const handleConfirmPasswordChange = (value: string) => {
        const stripped = value.replace(/\s/g, "").slice(0, 15);
        setConfirmPassword(stripped);
        setErrors((prev) => ({
            ...prev,
            confirmPassword: undefined,
            api: undefined,
        }));
    };

    // Strict email format validation matching legacy ForgotPassword
    const validateEmail = (): boolean => {
        const nextErrors: ForgotPasswordFormErrors = {};
        const trimmed = email.trim();

        if (!trimmed) {
            nextErrors.email = "Email is required";
        } else if ((trimmed.match(/@/g) || []).length !== 1) {
            nextErrors.email = "Email must contain exactly one '@'";
        } else if (trimmed.length > 30) {
            nextErrors.email = "Email must not exceed 30 characters";
        } else if ((trimmed.match(/\d/g) || []).length > 6) {
            nextErrors.email = "Email must not contain more than 6 digits";
        } else {
            const [, domainPart] = trimmed.split("@");
            const domainDotCount = (domainPart?.match(/\./g) || []).length;
            if (domainDotCount < 1 || domainDotCount > 2) {
                nextErrors.email =
                    "Domain must have 1 or 2 dots (e.g. .com or .co.in)";
            } else if (
                !/^[a-zA-Z0-9.]+@[a-zA-Z0-9]+(\.[a-zA-Z0-9]+){1,2}$/.test(
                    trimmed
                )
            ) {
                nextErrors.email =
                    "Invalid email — only '@' and '.' are allowed as special characters";
            }
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    // Step 2 validation (OTP & password matching)
    const validateResetForm = (): boolean => {
        const nextErrors: ForgotPasswordFormErrors = {};

        if (!otp.trim()) {
            nextErrors.otp = "OTP is required";
        }

        if (!newPassword) {
            nextErrors.newPassword = "New Password is required";
        } else if (newPassword.length < 8) {
            nextErrors.newPassword = "Password must be at least 8 characters";
        }

        if (!confirmPassword) {
            nextErrors.confirmPassword = "Confirm Password is required";
        } else if (newPassword !== confirmPassword) {
            nextErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    // Step 1: Request OTP
    const handleSendOtp = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (validateEmail()) {
            dispatch(sendOtp({ email: email.trim() }));
        }
    };

    // Step 2: Reset Password
    const handleResetPassword = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (validateResetForm()) {
            dispatch(
                forgotPassword({
                    identifier: email.trim(),
                    otp: otp.trim(),
                    newPassword,
                })
            );
        }
    };

    // Return to Step 1 to change email or re-request OTP
    const handleBackToEmail = () => {
        setStep(1);
        setErrors({});
        dispatch(clearState());
    };

    return (
        <main className="forgot-password-page">
            <div className="forgot-password-shell">
                <section className="forgot-password-left">
                    <div className="forgot-password-content">
                        <button
                            type="button"
                            className="forgot-password-brand"
                            onClick={() => navigate(forgotPasswordRoutes.home)}
                            aria-label="Go to Salar home"
                        >
                            <img
                                src="/assets/images/logos/salarlogo.png"
                                alt={forgotPasswordContent.brandAlt}
                            />
                        </button>

                        <header className="forgot-password-heading">
                            <span className="forgot-password-eyebrow">
                                {forgotPasswordContent.eyebrow}
                            </span>

                            <div className="forgot-password-title-accent">
                                <span />
                                <b />
                                <span />
                            </div>

                            <h1>
                                {step === 1
                                    ? forgotPasswordContent.title
                                    : forgotPasswordContent.resetTitle}
                            </h1>

                            <p>
                                {step === 1
                                    ? forgotPasswordContent.subtitle
                                    : forgotPasswordContent.resetSubtitle}
                            </p>
                        </header>

                        {/* Step 2 Email Badge */}
                        {step === 2 && (
                            <div className="forgot-password-email-chip">
                                <Mail size={14} />
                                <span>OTP sent to: {email}</span>
                            </div>
                        )}

                        <form
                            className="forgot-password-form"
                            onSubmit={
                                step === 1 ? handleSendOtp : handleResetPassword
                            }
                            noValidate
                        >
                            {step === 1 ? (
                                <div className="forgot-password-fields-stack">
                                    <div
                                        className={`forgot-password-field ${
                                            errors.email ? "has-error" : ""
                                        }`}
                                    >
                                        <Mail size={18} strokeWidth={1.9} />
                                        <input
                                            type="text"
                                            value={email}
                                            onChange={(e) =>
                                                handleEmailChange(
                                                    e.target.value
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === " ")
                                                    e.preventDefault();
                                            }}
                                            placeholder={
                                                forgotPasswordContent.inputPlaceholder
                                            }
                                            autoComplete="email"
                                            maxLength={30}
                                            disabled={authloading}
                                            aria-label={
                                                forgotPasswordContent.inputPlaceholder
                                            }
                                        />
                                    </div>
                                    {errors.email && (
                                        <span className="forgot-password-error">
                                            {errors.email}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="forgot-password-fields-stack">
                                    {/* OTP Field */}
                                    <div>
                                        <div
                                            className={`forgot-password-field ${
                                                errors.otp ? "has-error" : ""
                                            }`}
                                        >
                                            <KeyRound
                                                size={18}
                                                strokeWidth={1.9}
                                            />
                                            <input
                                                type="text"
                                                value={otp}
                                                onChange={(e) =>
                                                    handleOtpChange(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === " ")
                                                        e.preventDefault();
                                                }}
                                                placeholder={
                                                    forgotPasswordContent.otpPlaceholder
                                                }
                                                disabled={
                                                    authloading || isResetSuccess
                                                }
                                                maxLength={10}
                                                aria-label={
                                                    forgotPasswordContent.otpPlaceholder
                                                }
                                            />
                                        </div>
                                        {errors.otp && (
                                            <span className="forgot-password-error">
                                                {errors.otp}
                                            </span>
                                        )}
                                    </div>

                                    {/* New Password Field */}
                                    <div>
                                        <div
                                            className={`forgot-password-field ${
                                                errors.newPassword
                                                    ? "has-error"
                                                    : ""
                                            }`}
                                        >
                                            <Lock
                                                size={18}
                                                strokeWidth={1.9}
                                            />
                                            <input
                                                type={
                                                    showNewPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                value={newPassword}
                                                onChange={(e) =>
                                                    handleNewPasswordChange(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === " ")
                                                        e.preventDefault();
                                                }}
                                                placeholder={
                                                    forgotPasswordContent.newPasswordPlaceholder
                                                }
                                                disabled={
                                                    authloading || isResetSuccess
                                                }
                                                maxLength={15}
                                                autoComplete="new-password"
                                                aria-label={
                                                    forgotPasswordContent.newPasswordPlaceholder
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="forgot-password-eye-btn"
                                                onClick={() =>
                                                    setShowNewPassword(
                                                        !showNewPassword
                                                    )
                                                }
                                                aria-label={
                                                    showNewPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                            >
                                                {showNewPassword ? (
                                                    <EyeOff size={16} />
                                                ) : (
                                                    <Eye size={16} />
                                                )}
                                            </button>
                                        </div>
                                        {errors.newPassword && (
                                            <span className="forgot-password-error">
                                                {errors.newPassword}
                                            </span>
                                        )}
                                    </div>

                                    {/* Confirm Password Field */}
                                    <div>
                                        <div
                                            className={`forgot-password-field ${
                                                errors.confirmPassword
                                                    ? "has-error"
                                                    : ""
                                            }`}
                                        >
                                            <Lock
                                                size={18}
                                                strokeWidth={1.9}
                                            />
                                            <input
                                                type={
                                                    showConfirmPassword
                                                        ? "text"
                                                        : "password"
                                                }
                                                value={confirmPassword}
                                                onChange={(e) =>
                                                    handleConfirmPasswordChange(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (e.key === " ")
                                                        e.preventDefault();
                                                }}
                                                placeholder={
                                                    forgotPasswordContent.confirmPasswordPlaceholder
                                                }
                                                disabled={
                                                    authloading || isResetSuccess
                                                }
                                                maxLength={15}
                                                autoComplete="new-password"
                                                aria-label={
                                                    forgotPasswordContent.confirmPasswordPlaceholder
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="forgot-password-eye-btn"
                                                onClick={() =>
                                                    setShowConfirmPassword(
                                                        !showConfirmPassword
                                                    )
                                                }
                                                aria-label={
                                                    showConfirmPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                            >
                                                {showConfirmPassword ? (
                                                    <EyeOff size={16} />
                                                ) : (
                                                    <Eye size={16} />
                                                )}
                                            </button>
                                        </div>
                                        {errors.confirmPassword && (
                                            <span className="forgot-password-error">
                                                {errors.confirmPassword}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Inline API Error */}
                            {errors.api && (
                                <div className="forgot-password-api-error">
                                    <AlertCircle size={15} />
                                    <span>{errors.api}</span>
                                </div>
                            )}

                            {/* Reset Success Feedback */}
                            {isResetSuccess && (
                                <div className="forgot-password-success">
                                    <CheckCircle2 size={16} strokeWidth={2} />
                                    <span>
                                        {
                                            forgotPasswordContent.resetSuccessMessage
                                        }
                                    </span>
                                </div>
                            )}

                            {/* Main Submit Button */}
                            <button
                                type="submit"
                                className={`forgot-password-submit ${
                                    isResetSuccess ? "is-sent" : ""
                                }`}
                                disabled={authloading || isResetSuccess}
                            >
                                {isResetSuccess ? (
                                    <CheckCircle2 size={17} strokeWidth={2} />
                                ) : (
                                    <Send size={17} strokeWidth={2} />
                                )}

                                <span>
                                    {authloading
                                        ? step === 1
                                            ? forgotPasswordContent.sendingOtpLabel
                                            : forgotPasswordContent.resettingPasswordLabel
                                        : isResetSuccess
                                        ? "Password Reset!"
                                        : step === 1
                                        ? forgotPasswordContent.sendOtpLabel
                                        : forgotPasswordContent.resetPasswordLabel}
                                </span>
                            </button>

                            {/* Step 2 Back to Email Button */}
                            {step === 2 && !isResetSuccess && (
                                <button
                                    type="button"
                                    className="forgot-password-btn-secondary"
                                    onClick={handleBackToEmail}
                                    disabled={authloading}
                                >
                                    <RotateCcw size={14} />
                                    <span>
                                        {forgotPasswordContent.backToEmailLabel}
                                    </span>
                                </button>
                            )}

                            {/* Back to Sign In Link */}
                            <button
                                type="button"
                                className="forgot-password-back"
                                onClick={() =>
                                    navigate(forgotPasswordRoutes.signIn)
                                }
                            >
                                <ArrowLeft size={15} strokeWidth={2} />
                                <span>
                                    {forgotPasswordContent.backToSignInLabel}
                                </span>
                            </button>
                        </form>

                        {/* Social Media Links */}
                        <div className="forgot-password-social">
                            <span>{forgotPasswordContent.socialTitle}</span>

                            <div className="forgot-password-social-row">
                                {forgotPasswordSocialLinks.map((social) => (
                                    <a
                                        key={social.id}
                                        href={social.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={social.label}
                                        title={social.label}
                                    >
                                        <i className={social.iconClass} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section
                    className="forgot-password-right"
                    aria-label="Salar shopping"
                >
                    <div className="forgot-password-visual">
                        <img
                            src="/assets/img/register/forgotPassword.jpg"
                            alt={forgotPasswordContent.heroAlt}
                        />
                        <div className="forgot-password-visual-overlay" />
                        <span className="forgot-password-orbit forgot-password-orbit-one" />
                        <span className="forgot-password-orbit forgot-password-orbit-two" />
                        <span className="forgot-password-shine" />
                    </div>
                </section>
            </div>
        </main>
    );
};

export default ForgotPasswordPage;
